import React from 'react';
import {
  Box,
  Typography,
  Pagination,
  Select,
  MenuItem,
  useTheme,
  alpha
} from '@mui/material';

interface PaginationControlsProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  itemLabel?: string;
  pageSizeOptions?: number[];
  position?: 'top' | 'bottom';
  showPageSizeSelector?: boolean;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'items',
  pageSizeOptions = [20, 50, 100, 200],
  position = 'bottom',
  showPageSizeSelector = true
}) => {
  const theme = useTheme();
  
  // Don't show pagination if there are no items at all
  if (totalItems === 0) {
    return null;
  }

  const startItem = ((currentPage - 1) * pageSize) + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        py: 2,
        px: 3,
        ...(position === 'top' ? {
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        } : {
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }),
        backgroundColor: alpha(theme.palette.primary.light, 0.02),
        flexWrap: 'wrap',
        gap: 2
      }}
    >
      {/* Page Size Selector */}
      {showPageSizeSelector ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Show:
          </Typography>
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            size="small"
            sx={{
              minWidth: '80px',
              borderRadius: '8px',
              '& .MuiSelect-select': {
                py: 0.5,
                fontSize: '0.875rem',
              }
            }}
          >
            {pageSizeOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">
            per page
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '160px' }} />
      )}
      
      {/* Pagination Controls */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flex: 1 }}>
        {totalPages > 1 ? (
          <Pagination 
            count={totalPages} 
            page={currentPage} 
            onChange={onPageChange}
            color="primary"
            size="medium"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: '8px',
                fontWeight: 500,
                transition: theme.transitions.create(['background-color', 'transform']),
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                },
              },
              '& .Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }}
          />
        ) : (
          <Box sx={{ height: '32px' }} /> // Spacer to maintain consistent height
        )}
        <Typography variant="caption" color="text.secondary">
          Showing {startItem} - {endItem} of {totalItems} {itemLabel}
        </Typography>
      </Box>
      
      {/* Right spacer for balance */}
      <Box sx={{ width: '160px' }} />
    </Box>
  );
};

export default PaginationControls;

